import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { recorderAssignmentApi, adminUsersApi } from '../../api/recorderAssignmentApi';
import type { Competition } from '../../types/domain';

interface Props {
  competition: Competition;
}

export const CompetitionRecordersTab = ({ competition }: Props) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [adding, setAdding] = useState(false);

  const { data: assigned = [] } = useQuery({
    queryKey: ['admin', 'competition', competition.id, 'recorders'],
    queryFn: () => recorderAssignmentApi.list(competition.id),
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: adminUsersApi.list,
  });

  const assignedIds = new Set(assigned.map((u) => u.id));
  const candidates = allUsers.filter(
    (u) => (u.role === 'RECORDER' || u.role === 'UNIT_ADMIN') && !assignedIds.has(u.id) && u.isActive,
  );

  const assignMutation = useMutation({
    mutationFn: (userId: number) => recorderAssignmentApi.assign(competition.id, userId),
    onSuccess: () => {
      enqueueSnackbar('Zapisovatel přiřazen', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competition.id, 'recorders'] });
      setAdding(false);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => recorderAssignmentApi.remove(competition.id, userId),
    onSuccess: () => {
      enqueueSnackbar('Zapisovatel odebrán', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competition.id, 'recorders'] });
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Přiřazení zapisovatelé</Typography>
            <Typography variant="body2" color="text.secondary">
              Mohou zadávat výsledky pro tuto soutěž.
            </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setAdding(true)}
            disabled={candidates.length === 0}
          >
            Přidat
          </Button>
        </Stack>

        <List>
          {assigned.map((u) => (
            <ListItem
              key={u.id}
              secondaryAction={
                <Tooltip title="Odebrat přístup">
                  <IconButton edge="end" color="error" onClick={() => removeMutation.mutate(u.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText
                primary={u.displayName}
                secondary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={u.role === 'UNIT_ADMIN' ? 'Admin' : 'Zapisovatel'} />
                    <Typography variant="caption">{u.username}</Typography>
                    {u.email && <Typography variant="caption" color="text.secondary">• {u.email}</Typography>}
                  </Stack>
                }
              />
            </ListItem>
          ))}
          {assigned.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Žádný zapisovatel není přiřazen. UNIT_ADMINi mají přístup vždy.
            </Typography>
          )}
        </List>
      </CardContent>

      <Dialog open={adding} onClose={() => setAdding(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Přidat zapisovatele</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Vyberte uživatele s rolí RECORDER nebo UNIT_ADMIN. Pokud potřebujete vytvořit nového, jděte do
              sekce Uživatelé.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Uživatel</InputLabel>
              <Select
                label="Uživatel"
                value=""
                onChange={(e) => assignMutation.mutate(Number(e.target.value))}
                disabled={assignMutation.isPending}
              >
                {candidates.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.displayName} ({u.username}) — {u.role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Zavřít</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
